<script setup lang="ts">
  import { COOKIE_OPTIONS } from '@/utils/constants/cookies'
  import { useOrigDestDataHelper } from '@/composables/orig-dest/useOrigDestDataHelper'
  import { useFsfResponseData } from '@/composables/fsf/useFsfResponseData'
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'

  const origDestDataHelper = useOrigDestDataHelper()
  const { applyFSFResponse } = useFsfResponseData()
  const nuxtApp = useNuxtApp()
  const fsfStore = useFsfStore()

  if (import.meta.server && nuxtApp.ssrContext?.event.context.fsfDataResponse) {
    await applyFSFResponse()
    delete nuxtApp.ssrContext.event.context.fsfDataResponse
  }

  onBeforeMount(() => {
    backUrl.value = getBackUrl()
  })

  onMounted(() => {
    origDestDataHelper.setOrigDestFromUrl()
  })

  const btMobileApp = useCookie<string>('btMobileApp', { path: '/', domain: COOKIE_OPTIONS.domain })
  const hideHeader = computed(() => btMobileApp.value !== undefined)

  const { isDesktop } = useBTBreakpoints()

  const { locale } = useI18n()
  const localePath = useLocalePath()
  const linkPath = computed(() => localePath('/index', locale.value))

  const route = useRoute()

  const logo = {
    background: 'green',
    icon: {
      alt: 'AirBaltic logo',
      src: 'airbaltic.svg'
    }
  }
  const backUrl = ref('')

  const hasError = computed(() => fsfStore.errorCodes.length > 0 || fsfStore.errorTexts.length > 0 || fsfStore.errorRef)
  const errorMsg = computed(() => {
    if (fsfStore.errorTexts.length > 0) {
      return fsfStore.errorTexts[0] + (fsfStore.errorRef ? ` (ref: ${fsfStore.errorRef})` : '')
    }

    return fsfStore.errorTexts[0]
  })

  const getBackUrl = () => {
    const referrer = document?.referrer

    if (!referrer) {
      return linkPath.value
    }

    const refUrl = new URL(referrer)

    if (referrer === window?.location?.href) {
      return linkPath.value
    }

    if (refUrl.origin === window?.location?.origin) {
      return refUrl.toString()
    }

    const eeUrl = new URL(`${locale.value}/fb/availability`, referrer)

    const queryObj = route.query

    if (queryObj && Object.keys(queryObj).length > 0) {
      const searchParams = new URLSearchParams()

      Object.entries(queryObj).forEach(([key, value]) => {
        searchParams.append(key, value as string)
      })

      eeUrl.search = searchParams.toString()
    }

    return eeUrl.href
  }

  const nuxtImage = useImage()

  const BACKGROUND_IMAGE_PATH = '/fallback-moodboard-background.jpg'
  const backgroundImageUrl = nuxtImage(BACKGROUND_IMAGE_PATH, {}, { provider: 'ipx' })
</script>
<template>
  <div
    class="flex h-full min-h-screen flex-col gap-8 bg-bt-blue-dark md:gap-6 md:pb-5"
    :class="{ 'pt-8': hideHeader }"
    :style="{
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover'
    }"
  >
    <header
      v-if="!hideHeader"
      class="container flex flex-row items-center justify-between px-4 md:px-0"
    >
      <MainHeaderLogo
        :logo="logo"
        provider="ipx"
      />

      <NuxtLink
        v-if="backUrl !== ''"
        :to="backUrl"
        class="flex items-center gap-4 align-middle text-white no-underline"
        external
      >
        <span v-if="isDesktop">{{ $t('back') }}</span>
        <div class="flex size-8 items-center justify-center rounded-full border border-white p-1 text-white">
          <Icon name="common/close" />
        </div>
      </NuxtLink>
    </header>

    <div
      v-if="hasError"
      class="container flex flex-col items-center justify-center gap-4 md:gap-6"
    >
      <UiAlert
        variant="error"
        :text="errorMsg"
      />
    </div>

    <slot />
  </div>
</template>
